/*
  dev-countup — counts a headline statistic up from 0 when it scrolls into view
  ("90%", "4.8/5.0", "1,000+", "$350", "50,000 ft", "7.4").

  Markup: wrap ONLY the plain-text value in `<data data-dev-countup>…</data>`.
  NOT a <span>: `.dev-heading-h* :is(…, span)` restyles spans inside headings
  with the heading's own size ladder, which would undo per-section overrides
  (the stat cards hold 56px at mobile where the H2 ladder says 40). <data> and
  the copy's custom element match no theme rule and have no UA styles.
  The final value is server-rendered and never changes, so no-JS visitors,
  crawlers and screen readers always get the real number and the element's box
  never changes size. While counting, the real text is painted transparent and
  an aria-hidden copy is drawn on top of it (absolutely positioned, so it takes
  no space). The copy is anchored the way the real text is laid out — the
  text-align of the stat's block: left-aligned stats keep their first glyph at
  the same x on every frame and count in the element's own figures (tabular
  figures would indent the leading digit); centred stats stay centred and
  right-aligned ones keep their suffix put, both in tabular figures so the
  growing number does not jitter. On the last frame the copy shows the exact
  final string in the element's own figures, then it is removed.

  - Runs once per page view, when the stat is ~40% visible.
  - Only values that are a number with an optional symbol prefix ($ € £ + ~ < >)
    and a non-numeric suffix ("%", "+", " ft", "+ years") are animated, plus the
    rating form "4.8/5.0". "24/7", "Day 14", "1-3", "10:30" are left alone.
  - Inside a stat-grid card whose section animates (data-dev-anim once|loop,
    see assets/dev-stat-anim.js) the count starts when that card starts playing,
    after the delay of the value's own entrance animation (the metric rows'
    "7.4" rises 1000ms into the card) — it never touches those animations.
  - prefers-reduced-motion: nothing happens. Theme setting "Animate statistic
    numbers" off: the attribute is not emitted, so nothing happens.
*/
(function () {
  if (window.devCountup) {
    window.devCountup.scan(document);
    return;
  }

  var DURATION = 1400;
  var VISIBLE = 0.4;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var NUM = /^(\s*[$€£+~<>]?\s?)((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)([\s\S]*)$/;
  var io = null;

  function parse(text) {
    var m = NUM.exec(text);
    if (!m) return null;
    var num = m[2];
    var rest = m[3];
    var dot = num.indexOf('.');
    var decimals = dot === -1 ? 0 : num.length - dot - 1;
    if (/^\d/.test(rest)) return null;
    if (/\d/.test(rest)) {
      // Only the rating form "4.8/5.0" may carry a second number.
      if (!decimals || !/^\s*\/\s*\d+(?:\.\d+)?\s*$/.test(rest)) return null;
    }
    var value = parseFloat(num.replace(/,/g, ''));
    if (!(value > 0)) return null;
    return { prefix: m[1], rest: rest, value: value, decimals: decimals, commas: num.indexOf(',') !== -1 };
  }

  function format(p, v) {
    var s = v.toFixed(p.decimals);
    if (p.commas) {
      var parts = s.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      s = parts.join('.');
    }
    return p.prefix + s + p.rest;
  }

  function textRect(node) {
    var r = document.createRange();
    r.selectNodeContents(node);
    var rects = r.getClientRects();
    return rects.length ? rects[0] : null;
  }

  // Put the copy exactly over the real glyphs (line-height/half-leading of an
  // absolutely positioned box differ from the inline's content area).
  function alignment(el) {
    var block = el.parentElement || el;
    var cs = getComputedStyle(block);
    var ta = cs.textAlign;
    var rtl = cs.direction === 'rtl';
    if (/center/.test(ta)) return 'center';
    if (/right/.test(ta) || (ta === 'end' && !rtl) || (ta === 'start' && rtl)) return 'right';
    return 'left';
  }

  function calibrate(st) {
    var o = st.overlay;
    var shift = st.align === 'center' ? 'calc(-50% + ' : '';
    o.style.transform = st.align === 'center' ? 'translateX(-50%)' : '';
    o.style.fontVariantNumeric = '';
    o.textContent = st.text;
    var a = st.textNode ? textRect(st.textNode) : null;
    var b = textRect(o);
    var dx = 0;
    var dy = 0;
    if (a && b) {
      dx = st.align === 'left' ? a.left - b.left : st.align === 'right' ? a.right - b.right : (a.left + a.right - b.left - b.right) / 2;
      dy = a.top - b.top;
    }
    o.style.transform = shift
      ? 'translate(' + shift + dx + 'px),' + dy + 'px)'
      : Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01
        ? 'translate(' + dx + 'px,' + dy + 'px)'
        : '';
    if (st.align !== 'left') o.style.fontVariantNumeric = 'tabular-nums';
  }

  function prime(el) {
    var st = el._devCountup;
    var cs = getComputedStyle(el);
    var o = document.createElement('dev-countup-copy');
    o.setAttribute('aria-hidden', 'true');
    o.className = 'dev-countup__copy';
    st.align = alignment(el);
    var anchor = st.align === 'left' ? 'left:0;' : st.align === 'right' ? 'right:0;' : 'left:50%;';
    o.style.cssText =
      'position:absolute;top:0;' + anchor + 'white-space:nowrap;pointer-events:none;line-height:normal;' +
      'color:' + cs.color + ';-webkit-text-fill-color:' + cs.webkitTextFillColor + ';';
    st.restore = {
      position: el.style.position,
      color: el.style.color,
      fill: el.style.webkitTextFillColor
    };
    el.style.position = 'relative';
    el.style.color = 'transparent';
    el.style.webkitTextFillColor = 'transparent';
    el.appendChild(o);
    st.overlay = o;
    calibrate(st);
    o.textContent = format(st.p, 0);
  }

  function finish(el) {
    var st = el._devCountup;
    if (st.overlay && st.overlay.parentNode) st.overlay.parentNode.removeChild(st.overlay);
    st.overlay = null;
    el.style.position = st.restore.position;
    el.style.color = st.restore.color;
    el.style.webkitTextFillColor = st.restore.fill;
    el.setAttribute('data-dev-countup-state', 'done');
  }

  function ease(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function start(el, delay) {
    var st = el._devCountup;
    if (!st || st.started) return;
    st.started = true;
    window.clearTimeout(st.fallback);
    if (st.mo) st.mo.disconnect();
    var els = st.watch._devCountupEls;
    var all = true;
    for (var i = 0; i < els.length; i++) if (!els[i]._devCountup.started) all = false;
    if (all && io) io.unobserve(st.watch);
    window.setTimeout(function () {
      if (!st.overlay) return;
      calibrate(st);
      el.setAttribute('data-dev-countup-state', 'run');
      var o = st.overlay;
      var t0 = null;
      function frame(now) {
        if (!st.overlay) return;
        if (t0 === null) t0 = now;
        var t = Math.min(1, (now - t0) / DURATION);
        if (t >= 1) {
          o.style.fontVariantNumeric = '';
          o.textContent = st.text;
          window.requestAnimationFrame(function () {
            finish(el);
          });
          return;
        }
        o.textContent = format(st.p, st.p.value * ease(t));
        window.requestAnimationFrame(frame);
      }
      window.requestAnimationFrame(frame);
    }, delay || 0);
  }

  // Delay of the value's own entrance animation inside a stat card (the metric
  // rows' value rises 1000ms + 200ms per row into the card's timeline).
  function entranceDelay(el, card) {
    for (var n = el; n && n !== card; n = n.parentElement) {
      var cs = getComputedStyle(n);
      if (cs.animationName && cs.animationName !== 'none') {
        var d = parseFloat(cs.animationDelay) || 0;
        return /ms/.test(cs.animationDelay.split(',')[0]) ? d : d * 1000;
      }
    }
    return 0;
  }

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          if (!e.isIntersecting || e.intersectionRatio < VISIBLE) continue;
          var els = (e.target._devCountupEls || []).slice();
          for (var j = 0; j < els.length; j++) {
            var el = els[j];
            var st = el._devCountup;
            if (st.started) continue;
            if (st.card) {
              // Card-synced: dev-stat-anim should start the card now; if it
              // never does (script missing), count anyway after a beat.
              if (!st.fallback) st.fallback = window.setTimeout(start.bind(null, el, 0), 900);
            } else {
              start(el, 0);
            }
          }
        }
      },
      { threshold: [0, VISIBLE] }
    );
    return io;
  }

  function setup(el) {
    if (el._devCountup) return;
    var text = el.textContent;
    var p = parse(text);
    if (!p) {
      el._devCountup = { skip: true };
      return;
    }
    var textNode = null;
    for (var c = el.firstChild; c; c = c.nextSibling) if (c.nodeType === 3 && /\d/.test(c.nodeValue)) textNode = c;
    var st = (el._devCountup = { p: p, text: text, textNode: textNode });

    var card = el.closest('.dev-stat-card');
    var grid = card && card.closest('[data-dev-anim]');
    var mode = grid && grid.getAttribute('data-dev-anim');
    if (mode === 'once' || mode === 'loop') st.card = card;

    st.watch = st.card || el.parentElement || el;
    (st.watch._devCountupEls = st.watch._devCountupEls || []).push(el);
    prime(el);

    if (st.card) {
      var check = function () {
        if (card.getAttribute('data-anim-state') === 'play') start(el, entranceDelay(el, card));
      };
      st.mo = new MutationObserver(check);
      st.mo.observe(card, { attributes: true, attributeFilter: ['data-anim-state'] });
      check();
    }
    if (!st.started) observer().observe(st.watch);
  }

  function scan(root) {
    if (reduce.matches || typeof IntersectionObserver === 'undefined') return;
    var list = (root || document).querySelectorAll('[data-dev-countup]');
    for (var i = 0; i < list.length; i++) setup(list[i]);
  }

  function teardown(root) {
    var list = root.querySelectorAll('[data-dev-countup]');
    for (var i = 0; i < list.length; i++) {
      var st = list[i]._devCountup;
      if (!st || st.skip) continue;
      window.clearTimeout(st.fallback);
      if (st.mo) st.mo.disconnect();
      if (io && st.watch) io.unobserve(st.watch);
    }
  }

  window.devCountup = { scan: scan };

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
})();
